/* eslint-disable @nx/enforce-module-boundaries */
import { Inject, Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, switchMap, tap } from 'rxjs';
import { Environment, ResponseData } from '@t-llevo/util';
import { HttpClient } from '@angular/common/http';
import { apiVehicleTypeRoutes as apiRoutes } from '../routes/api-vehicle-type-routes';
import { UploadImageService } from 'admin-drivers/src/app/api/services/upload-image.service';
import { VehicleColors } from '../../vehicle-types/const/VehicleColors';
import { EnergySources } from '../../vehicle-types/const/EnergySources';
import { VehicleTypeResponse } from 'admin-drivers/src/app/api/models/responses/VehicleTypeResponse';
import { VehicleTypeRequest } from 'admin-drivers/src/app/api/models/requests/VehicleTypeRequest';
import { BaseCrudService } from '@t-llevo/common-ui';
import { VehicleType } from '../../common/models/VehicleType';
import { VehicleTypeMapper } from '../mappers/VehicleTypeMapper';

@Injectable()
export class VehicleTypesService extends BaseCrudService<
  VehicleType,
  VehicleTypeResponse[]
> {
  private vehicleTypesSub = new BehaviorSubject<VehicleType[] | null>(null);
  vehicleTypes$ = this.vehicleTypesSub.asObservable();

  private uploadImgSrv = inject(UploadImageService);
  constructor(@Inject(Environment) env: Environment, http: HttpClient) {
    super(env, http);
  }

  override transform(data: VehicleTypeResponse[]) {
    const response: ResponseData<VehicleType> = {
      count: 0,
      results: [],
      limit: '',
      page: '',
    };

    data.forEach((type) => {
      response.results.push(VehicleTypeMapper(type));
    });

    return response;
  }

  createVehicleType(data: Omit<VehicleTypeRequest, 'photo'> & { photo: File }) {
    return this.uploadImgSrv.uploadVehicleTypeImage(data.photo).pipe(
      switchMap((obs) => {
        const url = this.env.apiUrl + apiRoutes.routeTypeVehicles;
        const params: VehicleTypeRequest = {
          ...data,
          photo: obs.data,
        };

        return this.http.post(url, params);
      })
    );
  }

  updateVehicleType(
    id: string,
    params: Omit<VehicleTypeRequest, 'photo'> & { photo: File }
  ) {
    return this.uploadImgSrv.uploadVehicleTypeImage(params.photo).pipe(
      switchMap((obs) => {
        const url = this.env.apiUrl + apiRoutes.routeTypeVehicles + `/${id}`;
        const patchParams = {
          ...params,
          photo: obs.data,
        };

        return this.http.patch(url, patchParams);
      })
    );
  }

  deleteVehicleTypeById(id: string) {
    const url = this.env.apiUrl + apiRoutes.routeTypeVehicles + id;
    return this.http.delete(url);
  }

  getVehicleTypes() {
    const url = this.env.apiUrl + apiRoutes.routeTypeVehicles;
    return this.http.get<VehicleTypeResponse[]>(url).pipe(
      map((data) => data.map((type) => VehicleTypeMapper(type))),
      tap((data) => this.vehicleTypesSub.next(data))
    );
  }

  getVehicleTypeById(id: string) {
    const url = this.env.apiUrl + apiRoutes.routeTypeVehicles + `/${id}`;
    return this.http
      .get<VehicleTypeResponse>(url)
      .pipe(map((data) => VehicleTypeMapper(data)));
  }

  get vehicleColors() {
    return VehicleColors;
  }

  get energySources() {
    return EnergySources;
  }

  getVehicleImageByUrl(url: string) {
    return this.uploadImgSrv.getImageByUrl(url);
  }

  changeVehicleTypeAvailability(type: VehicleType) {
    const url = this.env.apiUrl + apiRoutes.routeTypeVehicles + '/' + type.id;
    return this.http.patch(url, { available: !type.available });
  }
}
